import { useCallback, useEffect, useMemo, useState } from "react";

import { resolveStatusFilterExpandedIds } from "../lib/status-filter-match";

const NO_IDS: ReadonlySet<string> = new Set();
const NO_OVERRIDES: ReadonlyMap<string, boolean> = new Map();

interface StatusFilterExpansionInput {
  /** The selected statuses joined; empty when no Status filter is on. */
  statusFilterKey: string;
  /** The user's own expand state, kept as it was before the filter. */
  stored: ReadonlySet<string>;
  /** Rows the Status filter opens by default. */
  autoExpanded: ReadonlySet<string>;
  /** Toggles the stored state; used while no Status filter is on. */
  onToggleStored: (id: string) => void;
}

/**
 * Expand state for rows a Status filter opens. Clicks under a filter are kept
 * apart from the stored state and tied to that filter, so clearing or changing
 * it returns to what the user had expanded before.
 */
export function useStatusFilterExpansion({
  statusFilterKey,
  stored,
  autoExpanded,
  onToggleStored,
}: StatusFilterExpansionInput) {
  const [overrides, setOverrides] = useState<{
    statusFilterKey: string;
    expanded: ReadonlyMap<string, boolean>;
  }>(() => ({ statusFilterKey: "", expanded: NO_OVERRIDES }));
  const isFiltered = statusFilterKey !== "";
  // Clearing the filter ends every override, so re-applying it starts from
  // the filter's own expand state.
  useEffect(() => {
    if (!isFiltered) {
      setOverrides({ statusFilterKey: "", expanded: NO_OVERRIDES });
    }
  }, [isFiltered]);
  const currentOverrides =
    isFiltered && overrides.statusFilterKey === statusFilterKey
      ? overrides.expanded
      : NO_OVERRIDES;
  const expandedIds = useMemo(
    () =>
      resolveStatusFilterExpandedIds(
        stored,
        isFiltered ? autoExpanded : NO_IDS,
        currentOverrides,
      ),
    [autoExpanded, currentOverrides, isFiltered, stored],
  );

  const setOverride = useCallback(
    (id: string, isExpanded: boolean) => {
      setOverrides((current) => {
        const expanded = new Map(
          current.statusFilterKey === statusFilterKey ? current.expanded : [],
        );
        expanded.set(id, isExpanded);
        return { statusFilterKey, expanded };
      });
    },
    [statusFilterKey],
  );

  const toggle = useCallback(
    (id: string) => {
      if (!isFiltered) {
        onToggleStored(id);
        return;
      }
      setOverride(id, !expandedIds.has(id));
    },
    [expandedIds, isFiltered, onToggleStored, setOverride],
  );

  /** Opens a row even if the user collapsed it under the current filter. */
  const expand = useCallback(
    (id: string) => {
      if (isFiltered) setOverride(id, true);
    },
    [isFiltered, setOverride],
  );

  return { expandedIds, toggle, expand };
}
